/**
 * Library → Mine → «Тексты»: every text the learner owns, linked to a deck or
 * not, most recent activity first (handoff §7 mobile, §19.5 desktop).
 *
 * A row opens the text's route. Its «···» menu holds «Изменить», «Поделиться» (the
 * share sheet, handoff §17.1) and «Удалить текст…». Deleting removes the text and never a card (ADR-115
 * Decision 2). Nothing here is a horizontal gesture or scroller, so a swipe that
 * starts on a row stays the chain's (ADR-106 Decision 4).
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { FileText, MoreHorizontal, Pencil, Plus, Share2, Trash2 } from 'lucide-react'

import { useLocale } from '../../i18n/context'
import { useDeleteText, useTextList } from '../../hooks/useTexts'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import { useExclusiveOverlay } from '../../hooks/useExclusiveOverlay'
import { useFlipMenuPlacement } from '../../hooks/useFlipMenuPlacement'
import { toast } from '../shared/Toast'
import TextDeleteConfirm from './TextDeleteConfirm'
import TextShareSheet from './share/TextShareSheet'
import GappedText from './GappedText'
import { SecondaryPill } from './ui'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'
import type { TextListItem } from '../../types'
import { practiceStatus } from '../../utils/textPractice'

// A text has three levels of its own (ADR-116 Decision 4); «{L} из 3».
const TEXT_LEVEL_MAX = 3

// handoff §19.5 draws flexible | 230 | 100 | 180 | 40 with a 16 gap on a body
// about 940 px wide. Library's content column is the narrow page width
// (Layout's --page-max-width-narrow, 680 px), where those widths leave the
// text column almost nothing, so the fixed columns are scaled to fit it: the
// text column keeps about a third of the row.
const DESKTOP_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 150px) 64px 116px 40px',
  columnGap: 12,
  alignItems: 'center',
}

const ROW_CSS = `
.text-row{cursor:pointer;transition:background .12s,border-color .12s}
.text-row:focus-visible{outline:2px solid var(--accent);outline-offset:2px}
@media (hover:hover){.text-row--desktop:hover{background:var(--row-plane-hover)!important;border-color:var(--row-border-hover)!important}}
.text-row-menu-item:hover,.text-row-menu-item:focus-visible{background:var(--bg-secondary);outline:none}
`

export default function TextList({ onAddText }: { onAddText: () => void }) {
  const { tr } = useLocale()
  const t = tr.textList
  const isDesktop = useIsDesktop()
  const { data, isLoading, isError, refetch } = useTextList()
  const [deleting, setDeleting] = useState<TextListItem | null>(null)
  const [sharing, setSharing] = useState<string | null>(null)

  if (isLoading) return <TextListSkeleton />
  if (isError || !data) {
    return (
      <div role="alert" className={CONTROL_SURFACE} style={{ textAlign: 'center', padding: '40px 16px', fontFamily: 'var(--font-ui)' }}>
        <p style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{t.loadFailed}</p>
        <SecondaryPill onClick={() => refetch()}>{t.retry}</SecondaryPill>
      </div>
    )
  }
  if (data.items.length === 0) return <TextListEmpty onAdd={onAddText} />

  return (
    // Rows are controls, not content: no OS selection, handles or long-press
    // menu on a title or preview (index.css, `.cs-ui`).
    <div data-testid="text-list" className={CONTROL_SURFACE} onContextMenu={suppressTouchContextMenu}>
      <style>{ROW_CSS}</style>
      {isDesktop && (
        <div aria-hidden style={{ ...DESKTOP_GRID, padding: '0 16px 8px' }}>
          {[t.colText, t.colDecks, t.colLevel, t.colPractice].map((label) => (
            <span key={label} style={eyebrowStyle}>{label}</span>
          ))}
        </div>
      )}
      <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.items.map((text) => (
          <li key={text.id}>
            <TextRow text={text} desktop={isDesktop} onDelete={() => setDeleting(text)} onShare={() => setSharing(text.id)} />
          </li>
        ))}
      </ul>
      {deleting && <DeleteFlow text={deleting} onClose={() => setDeleting(null)} />}
      {sharing && <TextShareSheet textId={sharing} onClose={() => setSharing(null)} />}
    </div>
  )
}

/** The delete confirmation for one listed text, and the delete itself. Mounted only while asking. */
export function DeleteFlow({ text, onClose, onDeleted }: {
  text: Pick<TextListItem, 'id' | 'decks'>
  onClose: () => void
  /** After the delete went through, for a screen that must leave the text it showed. */
  onDeleted?: () => void
}) {
  const { tr } = useLocale()
  const t = tr.textList
  const del = useDeleteText()
  const [failed, setFailed] = useState(false)
  return (
    <TextDeleteConfirm
      deckTitles={text.decks.map((d) => d.title)}
      pending={del.isPending}
      error={failed ? t.deleteFailed : null}
      onCancel={() => { if (!del.isPending) onClose() }}
      onConfirm={() => {
        setFailed(false)
        del.mutate(text.id, {
          onSuccess: () => { onClose(); toast.success(t.deleted); onDeleted?.() },
          onError: () => setFailed(true),
        })
      }}
    />
  )
}

/**
 * A row's «···»: one 40 px button and its menu: «Изменить», «Поделиться», «Удалить
 * текст…». Shared by the Library's rows, the deck screen's phone rows and the text
 * screen's strip.
 */
export function TextRowMenu({ title, onDelete, onEdit, onShare }: { title: string; onDelete: () => void; onEdit?: () => void; onShare?: () => void }) {
  const { tr } = useLocale()
  const items: MenuItem[] = [
    ...(onEdit ? [{ key: 'edit', label: tr.textEdit.menuEdit, icon: <Pencil size={15} aria-hidden />, onSelect: onEdit }] : []),
    ...(onShare ? [{ key: 'share', label: tr.textShare.share, icon: <Share2 size={15} aria-hidden />, onSelect: onShare }] : []),
    { key: 'delete', label: tr.textList.deleteText, icon: <Trash2 size={15} aria-hidden />, danger: true, onSelect: onDelete },
  ]
  return <OverflowMenu ariaLabel={tr.textList.moreFor(title)} items={items} />
}

export interface MenuItem {
  key: string
  label: string
  icon: ReactNode
  danger?: boolean
  onSelect: () => void
}

/** A 40 px «···» button and its small menu (ADR-106 P3: it joins the swipe block while open). */
export function OverflowMenu({ ariaLabel, items }: { ariaLabel: string; items: MenuItem[] }) {
  const [menuOpen, setMenuOpen] = useState(false)
  const menuAnchor = useRef<HTMLDivElement>(null)
  return (
    <div ref={menuAnchor} style={{ position: 'relative', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setMenuOpen((v) => !v)}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        style={{
          width: 40, height: 40, borderRadius: 'var(--radius-pill)', border: 'none', cursor: 'pointer',
          background: menuOpen ? 'var(--bg-secondary)' : 'transparent', color: 'var(--text-secondary)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <MoreHorizontal size={18} />
      </button>
      {menuOpen && (
        <RowMenu
          anchorRef={menuAnchor}
          onClose={() => setMenuOpen(false)}
          items={items.map((item) => ({ ...item, onSelect: () => { setMenuOpen(false); item.onSelect() } }))}
        />
      )}
    </div>
  )
}

// ── Row ─────────────────────────────────────────────────────────────────────

function TextRow({ text, desktop, onDelete, onShare }: { text: TextListItem; desktop: boolean; onDelete: () => void; onShare: () => void }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { tr, tag } = useLocale()
  const t = tr.textList
  const open = () => navigate(`/texts/${text.id}`, { state: { from: location.pathname + location.search } })
  const gapsLabel = text.gap_count > 0 ? t.gaps(text.gap_count) : t.noGaps
  const waiting = text.waiting_count > 0 ? ` · ${t.waiting(text.waiting_count)}` : ''
  // Mobile names the decks in the meta; desktop has a column of chips for them.
  const titles = text.decks.map((d) => d.title)
  const decksLabel = titles.length === 0 ? t.noDeck
    : titles.length === 1 ? t.deck(titles[0]) : t.decks(titles.join(', '))
  const meta = desktop ? `${gapsLabel}${waiting}` : `${gapsLabel} · ${decksLabel}${waiting}`

  const menu = (
    <TextRowMenu
      title={text.title}
      onDelete={onDelete}
      onEdit={() => navigate(`/texts/${text.id}/edit`, { state: { from: location.pathname + location.search } })}
      onShare={onShare}
    />
  )

  const titleEl = (
    <span style={{
      display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    }}>
      {text.title}
    </span>
  )
  const metaEl = (
    <span data-testid="text-row-meta" style={{ display: 'block', fontSize: 12, lineHeight: 1.4, color: 'var(--text-tertiary)' }}>
      {meta}
    </span>
  )

  const rowProps = {
    role: 'link' as const,
    tabIndex: 0,
    'aria-label': text.title,
    'data-testid': 'text-row',
    onClick: open,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.target !== e.currentTarget) return
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
    },
  }

  if (desktop) {
    return (
      <div
        {...rowProps}
        className="text-row text-row--desktop"
        style={{
          ...DESKTOP_GRID, padding: '14px 16px', borderRadius: 16,
          background: 'var(--bg-surface)', border: '1px solid var(--border)',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {titleEl}
          <Preview text={text} />
          {metaEl}
        </div>
        <DeckChips text={text} />
        <span style={{ fontFamily: 'var(--font-ui)' }}>
          {text.gap_count > 0 ? (
            <span aria-label={t.levelAria(text.level, TEXT_LEVEL_MAX)}>
              <span style={{ fontFamily: 'var(--font-content)', fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>
                {text.level}
              </span>
              <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}> {t.levelOf(TEXT_LEVEL_MAX)}</span>
            </span>
          ) : (
            <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{t.none}</span>
          )}
        </span>
        {/* A text without gaps has nothing to practise. */}
        <span data-testid="text-row-practice" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {practiceStatus(text.practice, text.gap_count, tr, tag) ?? t.none}
        </span>
        {menu}
      </div>
    )
  }

  return (
    <div
      {...rowProps}
      className="text-row"
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '12px 4px 12px 14px', borderRadius: 16,
        background: 'var(--bg-surface)', border: '1px solid var(--border)',
      }}
    >
      <span aria-hidden style={{
        width: 40, height: 40, flexShrink: 0, borderRadius: 8, background: 'var(--bg-secondary)',
        color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <FileText size={20} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {titleEl}
        <Preview text={text} />
        {metaEl}
      </div>
      {menu}
    </div>
  )
}

/** One line of the body, serif, gaps drawn as 40 px blanks with a dashed accent underline. */
function Preview({ text }: { text: TextListItem }) {
  return (
    <span
      data-testid="text-row-preview"
      style={{
        display: 'block', fontFamily: 'var(--font-content)', fontSize: 14, lineHeight: 1.5,
        color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}
    >
      <GappedText text={text.preview} spans={text.preview_gaps} blankWidth={40} blankTestId="text-row-blank" />
    </span>
  )
}

function DeckChips({ text }: { text: TextListItem }) {
  const { tr } = useLocale()
  const chip: CSSProperties = {
    display: 'inline-flex', alignItems: 'center', height: 28, padding: '0 10px', borderRadius: 'var(--radius-pill)',
    fontSize: 13, fontWeight: 500, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
    whiteSpace: 'nowrap', boxSizing: 'border-box',
  }
  if (text.decks.length === 0) {
    return (
      <div>
        <span style={{ ...chip, border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{tr.textList.noDeck}</span>
      </div>
    )
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, minWidth: 0 }}>
      {text.decks.map((d) => (
        <span key={d.id} title={d.title} style={{ ...chip, background: 'var(--bg-secondary)', color: 'var(--text-primary)' }}>
          {d.title}
        </span>
      ))}
    </div>
  )
}

function RowMenu({
  anchorRef, onClose, items,
}: {
  anchorRef: RefObject<HTMLDivElement | null>
  onClose: () => void
  items: MenuItem[]
}) {
  const ref = useRef<HTMLDivElement>(null)
  // ADR-106 P3: mounted only while open.
  useSwipeNavBlock(true, 'mine-text-menu')
  useExclusiveOverlay(true, onClose)
  useFlipMenuPlacement(true, anchorRef, ref)

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) && !anchorRef.current?.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    window.addEventListener('keydown', onKey)
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus()
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey) }
  }, [onClose, anchorRef])

  // A portal for the same reason as the deck rows' menu: a row clips nothing
  // here, but the placement hook writes viewport-fixed coordinates.
  return createPortal(
    <div
      ref={ref}
      role="menu"
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{
        position: 'fixed', zIndex: 100, minWidth: 200, overflow: 'hidden', padding: '4px 0',
        background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)',
        boxShadow: 'var(--shadow-float)',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {items.map((item) => (
        <button
          key={item.key}
          type="button"
          role="menuitem"
          className="text-row-menu-item"
          onClick={item.onSelect}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: '0 16px',
            background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left',
            fontFamily: 'var(--font-ui)', fontSize: 14, color: item.danger ? 'var(--danger)' : 'var(--text-primary)',
          }}
        >
          {item.icon}
          {item.label}
        </button>
      ))}
    </div>,
    document.body,
  )
}

function TextListEmpty({ onAdd }: { onAdd: () => void }) {
  const { tr } = useLocale()
  const t = tr.textList
  return (
    <div data-testid="text-list-empty" className={CONTROL_SURFACE} style={{ textAlign: 'center', padding: '48px 16px', fontFamily: 'var(--font-ui)' }}>
      <span aria-hidden style={{
        width: 48, height: 48, margin: '0 auto 14px', borderRadius: 12, background: 'var(--bg-secondary)',
        color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <FileText size={22} />
      </span>
      <p style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>{t.emptyTitle}</p>
      <p style={{ margin: '0 auto 18px', maxWidth: 340, fontSize: 14, lineHeight: 1.5, color: 'var(--text-secondary)' }}>
        {t.emptyBody}
      </p>
      <SecondaryPill onClick={onAdd}>
        <Plus size={16} aria-hidden />
        {t.emptyAdd}
      </SecondaryPill>
    </div>
  )
}

function TextListSkeleton() {
  return (
    <div aria-busy="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ height: 78, borderRadius: 16, background: 'var(--bg-secondary)', opacity: 0.6 }} />
      ))}
    </div>
  )
}

const eyebrowStyle: CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase',
  color: 'var(--text-tertiary)', fontFamily: 'var(--font-ui)',
}
